import { Outlet, Route, Routes } from "react-router";
import { AdminLayout } from "./components/AdminLayout";
import { AppLayout } from "./components/AppLayout";
import { AuthLayout } from "./components/AuthLayout";
import { RequireAdmin } from "./components/RequireAdmin";
import { RequireAuth } from "./components/RequireAuth";
import { RequirePlatformAuth } from "./components/RequirePlatformAuth";
import { RequirePlatformGuest } from "./components/RequirePlatformGuest";
import { RequireTos } from "./components/RequireTos";
import { RequireVerified } from "./components/RequireVerified";
import { ValidateWorkspace } from "./components/ValidateWorkspace";
import { AdminAuthProvider } from "./context/admin-auth";
import { AuthProvider } from "./context/auth";
import { FeatureFlagProvider } from "./context/feature-flags";
import { NotificationProvider } from "./context/notifications";
import { OnboardingProvider } from "./context/onboarding";
import { PlatformAuthProvider } from "./context/platform-auth";
import { RealtimeProvider } from "./context/realtime";
import { AcceptTermsPage } from "./pages/AcceptTermsPage";
import { AccountPage } from "./pages/AccountPage";
import { ApiKeysPage } from "./pages/ApiKeysPage";
import { AdminAuditLogPage } from "./pages/admin/AdminAuditLogPage";
import { AdminChangelogFormPage } from "./pages/admin/AdminChangelogFormPage";
import { AdminChangelogListPage } from "./pages/admin/AdminChangelogListPage";
import { AdminDashboardPage } from "./pages/admin/AdminDashboardPage";
import { AdminDlqPage } from "./pages/admin/AdminDlqPage";
import { AdminFlagsPage } from "./pages/admin/AdminFlagsPage";
import { AdminRateLimitsPage } from "./pages/admin/AdminRateLimitsPage";
import { AdminSignInPage } from "./pages/admin/AdminSignInPage";
import { AdminTenantDetailPage } from "./pages/admin/AdminTenantDetailPage";
import { AdminTenantsPage } from "./pages/admin/AdminTenantsPage";
import { BillingPage } from "./pages/BillingPage";
import { ConfirmDeletionPage } from "./pages/ConfirmDeletionPage";
import { CreateWorkspacePage } from "./pages/CreateWorkspacePage";
import { DeliveryLogPage } from "./pages/DeliveryLogPage";
import { FilesPage } from "./pages/FilesPage";
import { HomePage } from "./pages/HomePage";
import { InviteAcceptPage } from "./pages/InviteAcceptPage";
import { NotFoundPage } from "./pages/NotFoundPage";
import { NotificationPreferencesPage } from "./pages/NotificationPreferencesPage";
import { PlatformSignInPage } from "./pages/PlatformSignInPage";
import { PlatformSignUpPage } from "./pages/PlatformSignUpPage";
import { ProjectsPage } from "./pages/ProjectsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { TeamSettingsPage } from "./pages/TeamSettingsPage";
import { WebhooksPage } from "./pages/WebhooksPage";
import { WelcomePage } from "./pages/WelcomePage";
import { WorkspacesPage } from "./pages/WorkspacesPage";

export function App() {
	return (
		<Routes>
			<Route
				path="/"
				element={<h1 className="text-4xl font-bold text-center mt-20">Apsu</h1>}
			/>
			<Route
				path="sign-up"
				element={
					<PlatformAuthProvider>
						<RequirePlatformGuest />
					</PlatformAuthProvider>
				}
			>
				<Route element={<AuthLayout />}>
					<Route index element={<PlatformSignUpPage />} />
				</Route>
			</Route>
			<Route
				path="sign-in"
				element={
					<PlatformAuthProvider>
						<RequirePlatformGuest />
					</PlatformAuthProvider>
				}
			>
				<Route element={<AuthLayout />}>
					<Route index element={<PlatformSignInPage />} />
				</Route>
			</Route>
			<Route
				path="workspaces"
				element={
					<PlatformAuthProvider>
						<RequirePlatformAuth />
					</PlatformAuthProvider>
				}
			>
				<Route index element={<WorkspacesPage />} />
				<Route path="new" element={<CreateWorkspacePage />} />
			</Route>
			<Route
				path="/admin"
				element={
					<AdminAuthProvider>
						<Outlet />
					</AdminAuthProvider>
				}
			>
				<Route element={<AuthLayout />}>
					<Route path="sign-in" element={<AdminSignInPage />} />
				</Route>
				<Route element={<RequireAdmin />}>
					<Route element={<AdminLayout />}>
						<Route index element={<AdminDashboardPage />} />
						<Route path="tenants" element={<AdminTenantsPage />} />
						<Route path="tenants/:slug" element={<AdminTenantDetailPage />} />
						<Route path="changelog" element={<AdminChangelogListPage />} />
						<Route path="changelog/new" element={<AdminChangelogFormPage />} />
						<Route
							path="changelog/:slug/edit"
							element={<AdminChangelogFormPage />}
						/>
						<Route path="audit-log" element={<AdminAuditLogPage />} />
						<Route path="flags" element={<AdminFlagsPage />} />
						<Route path="rate-limits" element={<AdminRateLimitsPage />} />
						<Route path="webhooks" element={<AdminDlqPage />} />
						<Route path="webhooks/dlq/:slug" element={<AdminDlqPage />} />
					</Route>
				</Route>
			</Route>
			<Route
				path="invite/:token"
				element={
					<PlatformAuthProvider>
						<InviteAcceptPage />
					</PlatformAuthProvider>
				}
			/>
			<Route
				path=":workspaceSlug"
				element={
					<AuthProvider>
						<Outlet />
					</AuthProvider>
				}
			>
				<Route path="confirm-deletion" element={<ConfirmDeletionPage />} />
				<Route element={<ValidateWorkspace />}>
					<Route element={<RequireAuth />}>
						<Route element={<RequireVerified />}>
							<Route path="accept-terms" element={<AcceptTermsPage />} />
							<Route element={<RequireTos />}>
								<Route
									element={
										<NotificationProvider>
											<RealtimeProvider>
												<FeatureFlagProvider>
													<OnboardingProvider>
														<AppLayout />
													</OnboardingProvider>
												</FeatureFlagProvider>
											</RealtimeProvider>
										</NotificationProvider>
									}
								>
									<Route index element={<HomePage />} />
									<Route path="welcome" element={<WelcomePage />} />
									<Route path="billing" element={<BillingPage />} />
									<Route path="account" element={<AccountPage />} />
									<Route path="projects" element={<ProjectsPage />} />
									<Route path="settings" element={<SettingsPage />} />
									<Route path="files" element={<FilesPage />} />
									<Route path="settings/team" element={<TeamSettingsPage />} />
									<Route
										path="settings/notifications"
										element={<NotificationPreferencesPage />}
									/>
									<Route path="settings/webhooks" element={<WebhooksPage />} />
									<Route
										path="settings/webhooks/:endpointId/deliveries"
										element={<DeliveryLogPage />}
									/>
									<Route path="settings/api-keys" element={<ApiKeysPage />} />
								</Route>
							</Route>
						</Route>
					</Route>
				</Route>
			</Route>
			<Route path="*" element={<NotFoundPage />} />
		</Routes>
	);
}
